/**
 * SubscriptionAlert — big visible banner shown to users with admin-settings
 * access (admin, owner, clinical_director) when the organisation's SaaS
 * subscription is past_due or suspended.
 *
 * Rendered at the top of the Layout so it is visible on every page load.
 */
import React from 'react';
import { Alert, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface SubscriptionAlertProps {
  subscriptionStatus?: string;
  hasAdminSettingsAccess?: boolean;
}

const ALERT_STATUSES = new Set(['past_due', 'suspended']);

export default function SubscriptionAlert({
  subscriptionStatus,
  hasAdminSettingsAccess,
}: SubscriptionAlertProps) {
  const navigate = useNavigate();

  if (!subscriptionStatus || !ALERT_STATUSES.has(subscriptionStatus)) return null;
  if (!hasAdminSettingsAccess) return null;

  const isSuspended = subscriptionStatus === 'suspended';

  return (
    <Alert
      severity={isSuspended ? 'error' : 'warning'}
      variant="filled"
      sx={{ borderRadius: 0, justifyContent: 'center' }}
      action={
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/admin/settings?tab=subscription')}
          sx={{
            color: 'inherit',
            borderColor: 'rgba(255,255,255,0.5)',
            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
          }}
        >
          Fix Payment
        </Button>
      }
    >
      <Box component="span" sx={{ fontWeight: 600 }}>
        {isSuspended
          ? 'Your subscription has been suspended due to non-payment. Platform access is restricted.'
          : 'Your subscription payment has failed. Please update your payment method to avoid service interruption.'}
      </Box>
    </Alert>
  );
}
